import { useTheme } from '@table-library/react-table-library/theme'
import { Table, Header, HeaderRow, HeaderCell, Body, Row, Cell } from '@table-library/react-table-library/table'
import { Link, useSearchParams } from 'react-router-dom'
import { FiFolder } from 'react-icons/fi'
import { HasAccess } from '@permify/react-role'
import { LinkWithQuery } from '@/shared/components/link-with-query'
import { attachment } from '@/router/utils'
import { StatusChip } from '@/components/status-chip'
import { EditButton } from '@/shared/components/action-buttons'
import { Chip } from '@material-tailwind/react'
import { usePagination } from '@table-library/react-table-library/pagination'
import { Pagination } from '@/shared/components/pagination'
import PropTypes from 'prop-types'

const activeFields = (fields) => Array.isArray(fields) ? fields.filter((field) => !field?.archived) : []

const hasFieldValue = (value) => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0

  return true
}

const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Sí' : 'No'
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object') return JSON.stringify(value)

  return String(value)
}

const AssetData = ({ asset }) => {
  const categoryDefinitions = activeFields(asset.asset_type?.asset_category?.custom_fields)
    .filter((field) => hasFieldValue(asset.category_fields?.[field.key]))
  const typeDefinitions = activeFields(asset.asset_type?.custom_fields)
    .filter((field) => hasFieldValue(asset.type_fields?.[field.key]))

  if (categoryDefinitions.length === 0 && typeDefinitions.length === 0) return '—'

  return (
    <div className='flex flex-col gap-1 py-1'>
      {categoryDefinitions.map((field) => (
        <div key={`category-${field.key}`} className='leading-tight break-words'>
          <span>{field.label || field.key}: </span>
          <span>{formatFieldValue(asset.category_fields?.[field.key])}</span>
        </div>
      ))}
      {typeDefinitions.map((field) => (
        <div key={`type-${field.key}`} className='leading-tight break-words'>
          <span>{field.label || field.key}: </span>
          <span>{formatFieldValue(asset.type_fields?.[field.key])}</span>
        </div>
      ))}
    </div>
  )
}

const fieldDefinitionPropType = PropTypes.shape({
  archived: PropTypes.bool,
  key: PropTypes.string.isRequired,
  label: PropTypes.string
})

AssetData.propTypes = {
  asset: PropTypes.shape({
    asset_type: PropTypes.shape({
      asset_category: PropTypes.shape({
        custom_fields: PropTypes.arrayOf(fieldDefinitionPropType)
      }),
      custom_fields: PropTypes.arrayOf(fieldDefinitionPropType)
    }),
    category_fields: PropTypes.object,
    type_fields: PropTypes.object
  }).isRequired
}

export const AssetV2Table = ({ assets = [], meta, loading }) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const formatDateDMY = (s) => {
    if (!s) return ''
    const [y, m, d] = s.split('-')
    return `${d}-${m}-${y}`
  }

  const theme = useTheme({
    Table: `
      --data-table-library_grid-template-columns: repeat(8, 1fr);
    `
  })

  const onPaginationChange = (_, state) => {
    const page = state.page + 1
    const perPage = searchParams.get('per_page') || 10

    const next = new URLSearchParams(searchParams)
    next.set('page', page)
    next.set('per_page', perPage)
    setSearchParams(next)
  }

  const pagination = usePagination(
    { nodes: assets },
    {
      state: {
        page: (Number(searchParams.get('page')) || 1) - 1,
        size: Number(searchParams.get('per_page')) || 10
      },
      onChange: onPaginationChange
    },
    { isServer: true }
  )

  const nodes = { nodes: assets }

  if (loading) return <div>Cargando...</div>

  return (
    <div className='relative flex flex-col justify-between gap-4 h-full'>
      <Table
        data={nodes}
        pagination={pagination}
        layout={{ custom: true, fixedHeader: true }}
        theme={theme}
      >
        {(tableList) => (
          <>
            <Header>
              <HeaderRow>
                <HeaderCell>Tipo</HeaderCell>
                <HeaderCell>Identificador</HeaderCell>
                <HeaderCell>Contrato</HeaderCell>
                <HeaderCell>Datos de Activo</HeaderCell>
                <HeaderCell>Estado</HeaderCell>
                <HeaderCell>Acreditado</HeaderCell>
                <HeaderCell>NC</HeaderCell>
                <HeaderCell>Acciones</HeaderCell>
              </HeaderRow>
            </Header>
            <Body>
              {tableList.map((asset) => (
                <Row key={asset.id} item={asset}>
                  <Cell>{asset.asset_type?.name}</Cell>
                  <Cell>
                    <div className='flex flex-col'>
                      <span className='text-xs text-gray-500'>
                        {asset.asset_type?.identifier_label || 'Identificador'}
                      </span>
                      <span>{asset.identifier_value}</span>
                    </div>
                  </Cell>
                  <Cell>
                    <div className='flex flex-col'>
                      <span>{asset.contractor_contract?.id} - {asset.contractor_contract?.name}</span>
                      <span className='text-gray-600 text-sm'>{asset.contractor_contract?.company?.name}</span>
                    </div>
                  </Cell>
                  <Cell><AssetData asset={asset} /></Cell>
                  <Cell>
                    <StatusChip
                      value={asset.status}
                      options={{ active: 'green', inactive: 'red' }}
                    />
                  </Cell>
                  <Cell>
                    <div className='flex flex-col items-start gap-1'>
                      <Chip
                        variant='ghost'
                        color={asset.accredited === 'active' ? 'green' : 'red'}
                        size='sm'
                        className='capitalize text-center h-fit w-fit'
                        value={asset.accredited === 'active' ? 'Sí' : 'No'}
                      />
                      {asset.accredited === 'active' && asset.accreditation_expiration && (
                        <div className='flex flex-col leading-tight'>
                          <span className='text-xs text-gray-700'>Vence:</span>
                          <span className='text-xs text-gray-700'>
                            {formatDateDMY(asset.accreditation_expiration)}
                          </span>
                        </div>
                      )}
                    </div>
                  </Cell>
                  <Cell>{asset.nonconformities}</Cell>
                  <Cell>
                    <div className='flex gap-3'>
                      <HasAccess permissions={['asset_v2#edit']}>
                        <Link to={`/assets_v2/edit/${asset.id}`}>
                          <EditButton size='sm' />
                        </Link>
                      </HasAccess>
                      <HasAccess permissions={['asset_v2#attachments']}>
                        <LinkWithQuery to={attachment('assets_v2', asset.id)}>
                          <FiFolder className='cursor-pointer' size='1rem' />
                        </LinkWithQuery>
                      </HasAccess>
                    </div>
                  </Cell>
                </Row>
              ))}
            </Body>
          </>
        )}
      </Table>
      <Pagination
        onPageChange={({ selected }) => pagination.fns.onSetPage(selected)}
        page={pagination.state.page}
        totalPage={meta?.pagination?.total_pages || 0}
      />
    </div>
  )
}

AssetV2Table.propTypes = {
  assets: PropTypes.array,
  meta: PropTypes.shape({
    pagination: PropTypes.shape({
      total_pages: PropTypes.number
    })
  }),
  loading: PropTypes.bool
}
